import React, { useState } from 'react';
import { ChevronDown, ChevronUp, LineChart, Pin, PinOff, Trash2 } from 'lucide-react';
import {
  PortalMetric,
  PortalMetricDirection,
  PortalMetricFormat,
  portalAdminService,
} from '../../services/api';

const fa = (n: number) => n.toLocaleString('fa-IR');
const inputClass =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none transition focus:border-blue-500';

const DIRECTION_LABELS: Record<PortalMetricDirection, string> = {
  UP: 'بیشتر بهتر است',
  DOWN: 'کمتر بهتر است',
  NEUTRAL: 'فقط ثبت شود',
};

const FORMAT_LABELS: Record<PortalMetricFormat, string> = {
  NUMBER: 'عدد ساده',
  CURRENCY: 'مبلغ (تومان)',
  PERCENT: 'درصد',
  DURATION: 'مدت زمان',
};

interface Props {
  metrics: PortalMetric[];
  onChanged: () => void | Promise<void>;
  onError: (err: any, fallback: string) => void;
  onFlash: (message: string) => void;
}

/**
 * مدیریت شاخص‌های دوره.
 *
 * شاخص را می‌شود از داخل فرم تسک هم ساخت، ولی واحد و جهت و فرمتش فقط اینجا
 * تنظیم می‌شود — همان‌جایی که همهٔ شاخص‌ها کنار هم دیده می‌شوند.
 */
export const PortalMetricsPanel: React.FC<Props> = ({
  metrics,
  onChanged,
  onError,
  onFlash,
}) => {
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const patch = async (metric: PortalMetric, data: Partial<PortalMetric>, note?: string) => {
    setBusyId(metric.id);
    try {
      await portalAdminService.updateMetric(metric.id, data);
      await onChanged();
      if (note) onFlash(note);
    } catch (err: any) {
      onError(err, 'به‌روزرسانی شاخص انجام نشد.');
    } finally {
      setBusyId(null);
    }
  };

  /**
   * حذف دو مرحله‌ای: تلاش اول بدون force است تا اگر فیلدی وصل باشد سرور
   * هشدار بدهد و کاربر آگاهانه تصمیم بگیرد، نه اینکه نمودارش بی‌صدا خالی شود.
   */
  const remove = async (metric: PortalMetric) => {
    setBusyId(metric.id);
    try {
      await portalAdminService.deleteMetric(metric.id);
      await onChanged();
      onFlash('شاخص حذف شد.');
    } catch (err: any) {
      const message = err?.response?.data?.message;
      const linked = metric.linkedFieldCount ?? 0;
      if (linked > 0 && typeof message === 'string') {
        if (window.confirm(message + '\n\nحذف شود؟')) {
          try {
            await portalAdminService.deleteMetric(metric.id, true);
            await onChanged();
            onFlash('شاخص حذف شد؛ پاسخ‌های ثبت‌شده دست‌نخورده ماندند.');
          } catch (e: any) {
            onError(e, 'حذف شاخص انجام نشد.');
          }
        }
      } else {
        onError(err, 'حذف شاخص انجام نشد.');
      }
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="rounded-xl border border-indigo-100 bg-indigo-50/40">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2 p-3 text-right"
      >
        <LineChart className="h-4 w-4 shrink-0 text-indigo-500" />
        <span className="flex-1 text-sm font-bold text-indigo-900">
          شاخص‌های قابل‌پیگیری
        </span>
        <span className="shrink-0 rounded-full bg-white px-2 py-0.5 text-[10.5px] font-bold text-indigo-700">
          {fa(metrics.length)} شاخص
        </span>
        {open ? (
          <ChevronUp className="h-4 w-4 shrink-0 text-indigo-400" />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-indigo-400" />
        )}
      </button>

      {open ? (
        <div className="space-y-2 border-t border-indigo-100 p-3">
          {metrics.length === 0 ? (
            <p className="text-xs leading-6 text-indigo-800/80">
              هنوز شاخصی تعریف نشده. موقع ساخت تسک، روی فیلدهای «پاسخ عددی» و «طیف
              امتیازدهی» می‌توانید شاخص جدید بسازید تا روند آن عدد در طول دوره دنبال شود.
            </p>
          ) : (
            metrics.map((m) => (
              <div
                key={m.id}
                className={
                  'rounded-lg border bg-white p-3 transition ' +
                  (m.isActive ? 'border-gray-200' : 'border-gray-200 opacity-60')
                }
              >
                <div className="mb-2 flex items-center gap-2">
                  {/* ذخیره فقط موقع خروج از فیلد، وگرنه هر کلید یک درخواست می‌شود */}
                  <input
                    defaultValue={m.title}
                    onBlur={(e) => {
                      const next = e.target.value.trim();
                      if (next && next !== m.title) patch(m, { title: next });
                    }}
                    className="min-w-0 flex-1 rounded-lg border border-transparent px-2 py-1 text-sm font-bold text-gray-800 outline-none transition hover:border-gray-200 focus:border-blue-500"
                  />
                  <span className="shrink-0 rounded bg-gray-100 px-2 py-0.5 font-mono text-[10px] text-gray-500">
                    {m.key}
                  </span>
                  <button
                    type="button"
                    disabled={busyId === m.id}
                    onClick={() =>
                      patch(
                        m,
                        { isPinned: !m.isPinned },
                        m.isPinned ? 'از نمای کلی برداشته شد.' : 'به نمای کلی سنجاق شد.',
                      )
                    }
                    className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100"
                    title={m.isPinned ? 'برداشتن از نمای کلی' : 'سنجاق به نمای کلی'}
                  >
                    {m.isPinned ? (
                      <Pin className="h-3.5 w-3.5 text-indigo-600" />
                    ) : (
                      <PinOff className="h-3.5 w-3.5" />
                    )}
                  </button>
                  <button
                    type="button"
                    disabled={busyId === m.id}
                    onClick={() => remove(m)}
                    className="shrink-0 rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                    title="حذف شاخص"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>

                <div className="grid gap-2 md:grid-cols-3">
                  <label className="block">
                    <span className="mb-1 block text-[11px] font-bold text-gray-500">واحد</span>
                    <input
                      defaultValue={m.unit ?? ''}
                      placeholder="تومان، نفر، …"
                      onBlur={(e) => {
                        const next = e.target.value.trim();
                        if (next !== (m.unit ?? '')) patch(m, { unit: next });
                      }}
                      className={inputClass}
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-[11px] font-bold text-gray-500">جهت</span>
                    <select
                      value={m.direction}
                      onChange={(e) =>
                        patch(m, { direction: e.target.value as PortalMetricDirection })
                      }
                      className={inputClass}
                    >
                      {(Object.keys(DIRECTION_LABELS) as PortalMetricDirection[]).map((d) => (
                        <option key={d} value={d}>
                          {DIRECTION_LABELS[d]}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-[11px] font-bold text-gray-500">نمایش</span>
                    <select
                      value={m.format}
                      onChange={(e) => patch(m, { format: e.target.value as PortalMetricFormat })}
                      className={inputClass}
                    >
                      {(Object.keys(FORMAT_LABELS) as PortalMetricFormat[]).map((f) => (
                        <option key={f} value={f}>
                          {FORMAT_LABELS[f]}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-1.5 text-xs font-bold text-gray-600">
                    <input
                      type="checkbox"
                      checked={m.isActive}
                      onChange={(e) => patch(m, { isActive: e.target.checked })}
                    />
                    فعال
                  </label>
                  <span className="text-[11px] text-gray-500">
                    {(m.linkedFieldCount ?? 0) > 0
                      ? 'وصل به ' + fa(m.linkedFieldCount ?? 0) + ' فیلد تسک'
                      : 'هنوز به هیچ فیلدی وصل نشده'}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
};

export default PortalMetricsPanel;
